import React, { useState, useEffect } from 'react';
import { Package, Shield, Cpu, Activity, RefreshCw, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { getApiBaseUrl } from '@/services/api';

interface Metrics {
  total_orders: number;
  active_orders: number;
  completed_orders: number;
  pending_orders: number;
}

interface SystemHealth {
  status: string;
  systems: any[];
}

interface AgentMetrics {
  total_agents: number;
  active_agents: number;
  idle_agents: number;
  orchestrator_metrics?: {
    active_workflows: number;
  };
}

interface SystemOrders {
  gkpos: number;
  sap_ecc: number;
  hybris: number;
}

interface RecentActivity {
  id: string;
  agent_type: string;
  order_id: string;
  status: string;
  created_at: string;
  processing_time?: string;
}

const SimpleDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [agentMetrics, setAgentMetrics] = useState<AgentMetrics | null>(null);
  const [systemOrders, setSystemOrders] = useState<SystemOrders>({ gkpos: 0, sap_ecc: 0, hybris: 0 });
  const [uniqueOrderCount, setUniqueOrderCount] = useState<number>(0);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const API_BASE = getApiBaseUrl();

      // Load all data in parallel
      const [healthRes, agentsRes, gkposRes, sapEccRes, hybrisRes, monitorRes, verifyRes, executeRes, learnRes] = await Promise.allSettled([
        fetch(`${API_BASE}/simulators/health`),
        fetch(`${API_BASE}/agents/metrics`),
        fetch(`${API_BASE}/simulators/gkpos/orders?limit=1000`),
        fetch(`${API_BASE}/simulators/sap_ecc/orders?limit=1000`),
        fetch(`${API_BASE}/simulators/hybris/orders?limit=1000`),
        fetch(`${API_BASE}/agents/monitor/activities?limit=10`),
        fetch(`${API_BASE}/agents/verify/activities?limit=10`),
        fetch(`${API_BASE}/agents/execute/activities?limit=10`),
        fetch(`${API_BASE}/agents/learn/activities?limit=10`)
      ]);

      // Handle system health
      if (healthRes.status === 'fulfilled' && healthRes.value.ok) {
        const data = await healthRes.value.json();
        setSystemHealth(data);
      }

      // Handle agent metrics
      if (agentsRes.status === 'fulfilled' && agentsRes.value.ok) {
        const data = await agentsRes.value.json();
        setAgentMetrics(data.data || data);
      }

      // Calculate system-wise order counts and unique orders
      const allOrderIds = new Set<string>();
      const counts = { gkpos: 0, sap_ecc: 0, hybris: 0 };

      if (gkposRes.status === 'fulfilled' && gkposRes.value.ok) {
        const gkposOrders = await gkposRes.value.json();
        counts.gkpos = Array.isArray(gkposOrders) ? gkposOrders.length : 0;
        if (Array.isArray(gkposOrders)) {
          gkposOrders.forEach((order: any) => allOrderIds.add(order.order_id));
        }
      }

      if (sapEccRes.status === 'fulfilled' && sapEccRes.value.ok) {
        const sapEccOrders = await sapEccRes.value.json();
        counts.sap_ecc = Array.isArray(sapEccOrders) ? sapEccOrders.length : 0;
        if (Array.isArray(sapEccOrders)) {
          sapEccOrders.forEach((order: any) => allOrderIds.add(order.order_id));
        }
      }

      if (hybrisRes.status === 'fulfilled' && hybrisRes.value.ok) {
        const hybrisOrders = await hybrisRes.value.json();
        counts.hybris = Array.isArray(hybrisOrders) ? hybrisOrders.length : 0;
        if (Array.isArray(hybrisOrders)) {
          hybrisOrders.forEach((order: any) => allOrderIds.add(order.order_id));
        }
      }

      setSystemOrders(counts);
      setUniqueOrderCount(allOrderIds.size);

      // Combine activities from all agents
      const allActivities: any[] = [];
      
      const activityResponses = [
        { res: monitorRes, agent: 'monitor' },
        { res: verifyRes, agent: 'verify' },
        { res: executeRes, agent: 'execute' },
        { res: learnRes, agent: 'learn' }
      ];

      for (const { res, agent } of activityResponses) {
        if (res.status === 'fulfilled' && res.value.ok) {
          const data = await res.value.json();
          const activities = data.data?.activities || data.activities || [];
          allActivities.push(...activities.map((a: any) => ({ ...a, agent_type: agent })));
        }
      }

      // Sort by timestamp (most recent first) and take top 10
      allActivities.sort((a, b) => {
        const timeA = new Date(a.created_at || a.timestamp || 0).getTime();
        const timeB = new Date(b.created_at || b.timestamp || 0).getTime();
        return timeB - timeA;
      });
      
      setRecentActivities(allActivities.slice(0, 10));

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">NEXUS V3 Dashboard</h1>
              <p className="text-gray-600">AI-Powered Incident Management System</p>
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Unique Orders Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unique Orders</p>
                <p className="text-2xl font-bold text-gray-900">
                  {uniqueOrderCount}
                </p>
                <p className="text-xs text-blue-600">
                  Across all systems
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Systems Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">System Health</p>
                <p className="text-2xl font-bold text-gray-900">
                  3/3
                </p>
                <p className="text-xs text-green-600">
                  Online
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Agents Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">AI Agents</p>
                <p className="text-2xl font-bold text-gray-900">
                  4
                </p>
                <p className="text-xs text-purple-600">
                  Monitor, Verify, Execute, Learn
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Cpu className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Workflows Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Recent Activities</p>
                <p className="text-2xl font-bold text-gray-900">
                  {recentActivities.length}
                </p>
                <p className="text-xs text-orange-600">
                  Agent workflows
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* System Orders Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* GKPOS */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900">GKPOS</h3>
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-700">Total Orders</span>
                <span className="text-2xl font-bold text-blue-900">{systemOrders.gkpos}</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${(systemOrders.gkpos / Math.max(systemOrders.gkpos, systemOrders.sap_ecc, systemOrders.hybris, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* SAP ECC */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-green-900">SAP ECC</h3>
              <Shield className="h-6 w-6 text-green-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-700">Total Orders</span>
                <span className="text-2xl font-bold text-green-900">{systemOrders.sap_ecc}</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${(systemOrders.sap_ecc / Math.max(systemOrders.gkpos, systemOrders.sap_ecc, systemOrders.hybris, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Hybris */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-purple-900">Hybris</h3>
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-700">Total Orders</span>
                <span className="text-2xl font-bold text-purple-900">{systemOrders.hybris}</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full" 
                  style={{ width: `${(systemOrders.hybris / Math.max(systemOrders.gkpos, systemOrders.sap_ecc, systemOrders.hybris, 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Workflow Activities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Recent Agent Workflows</h2>
            <Activity className="h-6 w-6 text-gray-400" />
          </div>
          
          {recentActivities && recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.slice(0, 5).map((activity: any, index: number) => {
                const agentColors: Record<string, { bg: string; text: string; border: string }> = {
                  monitor: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
                  verify: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
                  execute: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
                  learn: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' }
                };
                
                // Extract order ID from activity metadata or workflow_id
                const orderId = activity.order_id || activity.metadata?.order_id || 
                  (activity.metadata?.workflow_id ? activity.metadata.workflow_id.split('-').slice(2, -1).join('-') : null) ||
                  activity.title?.split(' - ')[1] || 'N/A';
                
                // Use agent_type field
                const agentType = activity.type || activity.agent_type || 'monitor';
                const colors = agentColors[agentType] || agentColors.monitor;
                
                // Truncate description for display
                const shortDescription = activity.description 
                  ? (activity.description.length > 120 
                      ? activity.description.substring(0, 120) + '...' 
                      : activity.description)
                  : 'No analysis available';
                
                return (
                  <div key={activity.id || index} className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colors.text} border ${colors.border}`}>
                            {agentType.toUpperCase()} AGENT
                          </span>
                          {activity.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Clock className="h-4 w-4 text-orange-600" />
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            activity.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : activity.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {activity.status || 'pending'}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <p className={`text-sm font-medium ${colors.text}`}>
                            Order: <span className="font-bold">{orderId}</span>
                          </p>
                          
                          {/* AI Analysis Summary */}
                          <p className="text-xs text-gray-700 bg-white bg-opacity-50 p-2 rounded border border-gray-200">
                            {shortDescription}
                          </p>
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-600">
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {activity.duration || activity.processing_time || 'N/A'}
                            </span>
                            <span>
                              {activity.timestamp || activity.created_at 
                                ? new Date(activity.timestamp || activity.created_at).toLocaleTimeString() 
                                : 'Just now'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>No recent workflow activities</p>
              <p className="text-sm mt-2">Edit an order to trigger AI agents</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleDashboard;
